import React, { FormEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { track } from '@/analytics.js';
import { Body, Close, Content, Dialog, Head } from './dialog.styled.js';
import { accentButton, controlButton } from './button.styled.js';
import { CONTACT_PATH, MESSAGE_MAX, MESSAGE_MIN, MESSAGE_TYPES, TMessageType } from '@/utils/contact.js';

// The contact form, emailed to the site's owner by the worker (src/utils/contact.ts)

const Form = styled.form`
	display: flex;
	flex-direction: column;
	gap: 0.9em;
	padding-top: 0.75em;

	label {
		display: flex;
		flex-direction: column;
		gap: 0.3em;
		font-weight: 600;
		font-size: 0.9em;
	}

	select,
	input,
	textarea {
		box-sizing: border-box;
		width: 100%;
		padding: 0.55em 0.7em;
		border: 1px solid #ccc;
		border-radius: 0.5em;
		background: #fff;
		color: inherit;
		font: inherit;
		font-weight: 400;
		/* Below 16px, iOS zooms the page into a field it focuses */
		font-size: 16px;
	}

	textarea {
		min-height: 8em;
		resize: vertical;
	}
`;

// The field only bots fill in: off screen, out of the tab order, and named to tempt them
const Trap = styled.div`
	position: absolute;
	left: -10000px;
	width: 1px;
	height: 1px;
	overflow: hidden;
`;

const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1em;

	small {
		opacity: 0.7;
	}
`;

// Looks like the app's main action, Compare
const Send = styled.button`
	${controlButton}
	${accentButton}
	flex-shrink: 0;
	font-size: 13px;
`;

const Problem = styled.p`
	margin: 0;
	color: #c02;
`;

type TState = { status: 'editing' | 'sending' | 'sent' } | { status: 'failed'; error: string };

type TProps = {
	open: boolean;
	onClose: () => void;
};

export const ContactDialog = ({ open, onClose }: TProps) => {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const [type, setType] = useState<TMessageType>(MESSAGE_TYPES[0]);
	const [email, setEmail] = useState('');
	const [message, setMessage] = useState('');
	const [state, setState] = useState<TState>({ status: 'editing' });

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) return;
		if (open && !dialog.open) {
			dialog.showModal();
			track('contact_opened');
		}
		if (!open && dialog.open) dialog.close();
		// A form sent before starts afresh; one left half written keeps its text
		if (open && state.status === 'sent') {
			setMessage('');
			setState({ status: 'editing' });
		}
		// Only on opening and closing
	}, [open]);

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const website = new FormData(event.currentTarget).get('website');
		setState({ status: 'sending' });
		try {
			const response = await fetch(CONTACT_PATH, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ type, email, message, website }),
			});
			const result = (await response.json().catch(() => ({}))) as { error?: string };
			if (!response.ok)
				throw new Error(result.error ?? 'The message could not be sent: try again later');
			track('contact_sent', { type });
			setState({ status: 'sent' });
		} catch (error) {
			setState({
				status: 'failed',
				error: error instanceof Error ? error.message : 'The message could not be sent',
			});
		}
	};

	return (
		<Dialog
			ref={dialogRef}
			aria-labelledby="contact-title"
			onClose={onClose}
			// A press on the backdrop lands on the dialog itself, never on its content
			onClick={(event) => {
				if (event.target === event.currentTarget) onClose();
			}}
		>
			<Content>
				<Head>
					<h2 id="contact-title">
						<small>Contact</small>
						Write to All2Scale
					</h2>
					<Close type="button" onClick={onClose} aria-label="Close the contact form">
						×
					</Close>
				</Head>
				<Body>
					{state.status === 'sent' ? (
						<p role="status">Thank you! Your message is on its way.</p>
					) : (
						<Form onSubmit={handleSubmit}>
							<label>
								What is it about?
								<select
									value={type}
									onChange={(event) => setType(event.target.value as TMessageType)}
								>
									{MESSAGE_TYPES.map((option) => (
										<option key={option}>{option}</option>
									))}
								</select>
							</label>
							<label>
								Your email
								<input
									type="email"
									required
									autoComplete="email"
									value={email}
									onChange={(event) => setEmail(event.target.value)}
								/>
							</label>
							<label>
								Message
								<textarea
									required
									minLength={MESSAGE_MIN}
									maxLength={MESSAGE_MAX}
									value={message}
									onChange={(event) => setMessage(event.target.value)}
								/>
							</label>
							<Trap aria-hidden="true">
								<label>
									Website
									<input name="website" tabIndex={-1} autoComplete="off" />
								</label>
							</Trap>
							{state.status === 'failed' && <Problem role="alert">{state.error}</Problem>}
							<Footer>
								<small>Your email is used only to reply to you.</small>
								<Send type="submit" disabled={state.status === 'sending'}>
									{state.status === 'sending' ? 'Sending…' : 'Send'}
								</Send>
							</Footer>
						</Form>
					)}
				</Body>
			</Content>
		</Dialog>
	);
};
